/**
 * A run of a log line's message as the log pane shows it: text, or an http(s)
 * URL to link (specifications/services.md §Log pane: follow-tail). `offset` is
 * into the ANSI-stripped message, so it doubles as a React key.
 */
export type LogPart = {
  readonly link: boolean;
  readonly offset: number;
  readonly text: string;
};

const ESC = "\u001b";

// What follows an ESC: a CSI sequence (`[`, parameters, a final byte — SGR
// colour is one), an OSC string up to its BEL (an ST terminator is an ESC of
// its own), or a two-byte escape.
const SEQUENCE = /^(?:\[[0-?]*[ -/]*[@-~]|\][^\p{Cc}]*\p{Cc}?|.)/su;

// Colour is dropped rather than rendered, so a URL a process colours piece by
// piece comes out whole.
const stripAnsi = (message: string): string => {
  const [head = "", ...rest] = message.split(ESC);
  return head + rest.map((piece) => piece.replace(SEQUENCE, "")).join("");
};

// The scheme has to start a word — `git+https://` is a scheme of its own. A URL
// runs to whitespace, `<`, `>` or a control character.
const URL_CANDIDATE = /(?<![a-z0-9+.-])https?:\/\/[^\s<>\p{Cc}]+/giu;

const SENTENCE_PUNCTUATION = ".,;:!?";
const QUOTES = "\"'`";
const OPENER: Readonly<Record<string, string>> = {
  ")": "(",
  "]": "[",
  "}": "{",
};

const count = (s: string, c: string): number => s.split(c).length - 1;

// Hands back what the sentence around a URL put after it: trailing
// punctuation, and a closing bracket or quote the URL never opened. The
// scheme's `//` is never trimmed, so the loop ends there at the latest.
const trimUrl = (candidate: string): string => {
  let url = candidate;
  for (;;) {
    const last = url.at(-1)!;
    const opener = OPENER[last];
    const unbalanced =
      opener === undefined
        ? QUOTES.includes(last) && count(url, last) % 2 === 1
        : count(url, last) > count(url, opener);
    if (!SENTENCE_PUNCTUATION.includes(last) && !unbalanced) {
      return url;
    }
    url = url.slice(0, -1);
  }
};

/** The message, ANSI stripped, as parts that cover it in order. */
export const logMessageParts = (message: string): ReadonlyArray<LogPart> => {
  const text = stripAnsi(message);
  const parts: LogPart[] = [];
  let offset = 0;
  for (const match of text.matchAll(URL_CANDIDATE)) {
    const url = trimUrl(match[0]);
    if (url.endsWith("://")) {
      continue;
    }
    if (match.index > offset) {
      parts.push({
        link: false,
        offset,
        text: text.slice(offset, match.index),
      });
    }
    parts.push({ link: true, offset: match.index, text: url });
    offset = match.index + url.length;
  }
  if (offset < text.length) {
    parts.push({ link: false, offset, text: text.slice(offset) });
  }
  return parts;
};
